import { Alert, Button, Modal, Space, Typography } from 'antd';
import React, { useRef, useState } from 'react';

import { TagManager } from '@/components/common/TagManager';
import {
  CrepeMarkdownField,
  type CrepeMarkdownFieldHandle,
} from '@/components/editor/CrepeMarkdownField';
import { useAssetEditor } from '@/hooks/useAssetEditor';

const { Text } = Typography;

export interface CreateNoteModalProps {
  open: boolean;
  assetSessionId: string | null;
  accessToken?: string | null;
  clientId?: string;
  projectId?: string;
  onClose: () => void;
  onSaved: (cardId: string) => void;
}

export const CreateNoteModal: React.FC<CreateNoteModalProps> = ({
  open,
  assetSessionId,
  accessToken,
  clientId,
  projectId,
  onClose,
  onSaved,
}) => {
  const editorRef = useRef<CrepeMarkdownFieldHandle | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);

  const editor = useAssetEditor({
    assetSessionId: assetSessionId ?? '',
    accessToken,
    clientId,
    projectId,
  });

  const handleSave = async (): Promise<void> => {
    setSaveError(null);
    const latestMarkdown = editorRef.current?.getMarkdown() ?? editor.markdown;
    const result = await editor.save(latestMarkdown);
    if (result.error) {
      setSaveError(result.error);
      return;
    }
    if (result.saved && result.cardId) {
      onSaved(result.cardId);
    }
  };

  return (
    <Modal
      open={open}
      onCancel={onClose}
      width="80vw"
      title={
        <input
          aria-label="Note title"
          className="create-note-title-input"
          placeholder="Enter title..."
          value={editor.title}
          onChange={(e) => editor.setTitle(e.target.value)}
        />
      }
      styles={{
        content: {
          height: '80vh',
          display: 'flex',
          flexDirection: 'column',
          padding: 0,
          background: 'var(--bg-subtle)',
        },
        header: { padding: '12px 24px', flexShrink: 0, background: 'var(--bg-subtle)' },
        body: { flex: 1, overflowY: 'auto', padding: '16px 24px', background: 'var(--bg-subtle)' },
        footer: { padding: '12px 24px', flexShrink: 0, background: 'var(--bg-subtle)' },
      }}
      destroyOnHidden
      footer={
        <Space>
          <Button onClick={onClose}>Cancel</Button>
          <Button
            type="primary"
            loading={editor.isSaving}
            disabled={!editor.canSave}
            onClick={() => void handleSave()}
          >
            Save &amp; Link
          </Button>
        </Space>
      }
    >
      <Space direction="vertical" size="middle" style={{ width: '100%' }}>
        {editor.loadError && <Alert type="error" showIcon message={editor.loadError} />}
        {saveError && <Alert type="error" showIcon message={saveError} />}

        <TagManager
          tags={editor.tags}
          onTagsChange={editor.setTags}
          accessToken={accessToken ?? ''}
          clientId={clientId ?? ''}
          projectId={projectId}
          cardId={editor.persistedCardId}
          localOnly={!editor.persistedCardId}
        />

        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <Text type="secondary">
            Milkdown tip: use slash commands in the editor with <code>/</code>.
          </Text>
        </div>

        {editor.editorReady && assetSessionId ? (
          <CrepeMarkdownField
            ref={editorRef}
            bootKey={assetSessionId}
            defaultMarkdown={editor.markdown}
            onMarkdownChange={editor.setMarkdown}
            onReady={editor.hydrateMarkdown}
            minHeight={320}
          />
        ) : (
          <Text type="secondary">Loading…</Text>
        )}
      </Space>
    </Modal>
  );
};

export default CreateNoteModal;
